import React from "react";
import { NodeProps, useReactFlow } from "reactflow";
import { operationType } from "../../../../../services/types";
import { Main, Styled } from "./style";
import { Select } from "../../../../form/select";
import { updateNodeValue } from "../../../../../services/method";
import axios from "axios";
import { webURL } from "../../../../../services/API";
import { Checkboxs } from "../../../../form/checkboxs";
import { SettingsBlock, settingsDataType } from "../../../../settingsBlock";
import { IoCheckmarkCircleOutline, IoWarningOutline } from "react-icons/io5";
import { ExpandedBlock } from "../../../expanded";

type KnowledgeProps = NodeProps<operationType> & {
   setChildMinimize?: (fn: (id: string) => void) => void;
   setChildSettings?: (fn: (id: string) => void) => void;
};

const normalizeId = (value: unknown) => (value == null ? '' : String(value));

export function KnowledgeBase({ id, data, setChildMinimize, setChildSettings }: KnowledgeProps) {
   const { setNodes } = useReactFlow();
   const activeFolder = React.useMemo(
      () => data.value?.folders?.find((folder: any) => folder.isActive),
      [data.value?.folders]
   );
   const folderId = activeFolder?.id;
   const isAgentDecide = folderId === 'agent_decide';
   const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
   const [isExpanded, setIsExpanded] = React.useState(false);
   const [isLoadingFiles, setIsLoadingFiles] = React.useState(false);
   const [foldersLoaded, setFoldersLoaded] = React.useState(false);

   // --
   React.useEffect(() => {
      if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev) });
      if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })) });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [setChildMinimize, setChildSettings]);

   // Load folder list once, preserving any selection already on the node.
   React.useEffect(() => {
      let cancelled = false;
      (async function () {
         try {
            const response = (await axios.get(`${webURL}/knowledge`)).data;
            if (cancelled) return;

            setNodes(nodes => nodes.map(node => {
               if (node.id !== id) return node;
               const currentActiveId = normalizeId(
                  node.data?.value?.folders?.find((folder: any) => folder.isActive)?.id
               );
               const agentDecideOption = {
                  id: 'agent_decide',
                  name: 'Let AI Agent Decide',
                  isActive: currentActiveId === 'agent_decide',
               };
               const folders = [
                  agentDecideOption,
                  ...(Array.isArray(response) ? response : []).map((item: any) => ({
                     id: normalizeId(item._id),
                     name: item.name,
                     isActive: currentActiveId ? currentActiveId === normalizeId(item._id) : false,
                     isIndexed: Boolean(item.isIndexed),
                     indexingStatus: item.indexingStatus || (item.isIndexed ? 'completed' : 'idle'),
                  })),
               ];
               return {
                  ...node,
                  data: {
                     ...node.data,
                     value: { ...node.data.value, folders },
                  },
               };
            }));
         } catch {
            // Keep whatever folders were already on the node.
         } finally {
            if (!cancelled) setFoldersLoaded(true);
         }
      })();
      return () => { cancelled = true; };
   }, [id, setNodes]);

   // Load files for the active folder.
   React.useEffect(() => {
      if (!folderId || isAgentDecide) {
         if (isAgentDecide) updateNodeValue(id, setNodes, { files: [] });
         return;
      }

      let cancelled = false;
      (async function () {
         setIsLoadingFiles(true);
         try {
            const response = (await axios.get(`${webURL}/knowledge?folderId=${folderId}`)).data;
            if (cancelled) return;
            const files = Array.isArray(response?.files)
               ? response.files.map((file: any) => ({
                  id: normalizeId(file._id),
                  type: file.type,
                  name: file.name,
                  url: file.url,
                  isActive: true,
               }))
               : [];
            updateNodeValue(id, setNodes, { files });
         } catch {
            // Keep existing files on the node if refresh fails (avoid false empty-folder warning).
         } finally {
            if (!cancelled) setIsLoadingFiles(false);
         }
      })();

      return () => { cancelled = true; };
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [folderId, isAgentDecide]);

   const handleSelectFolder = (selectId: string | number) => {
      const selectedId = normalizeId(selectId);
      updateNodeValue(id, setNodes, {
         folders: (data.value?.folders || []).map((item: any) => ({
            ...item,
            isActive: normalizeId(item.id) === selectedId,
         })),
      });
   };

   const filesLengthActive = data.value?.files?.filter((item: any) => item.isActive)?.length || 0;
   const hasFolderSelected = !!folderId && !isAgentDecide;
   const indexingStatus = String(activeFolder?.indexingStatus || '').toLowerCase();
   const indexFailed = hasFolderSelected && indexingStatus === 'error';
   const isWarning = !isAgentDecide && (
      !hasFolderSelected
      || (!isLoadingFiles && foldersLoaded && !filesLengthActive)
      || indexFailed
   );

   return (<>
      <SettingsBlock nodeId={id} data={settings} setData={setSettings} />
      <Main>
         {isWarning ? <>
            <div id="warning"></div>
            <span id="warningIcon"><IoWarningOutline size={21} /></span>
         </> : data.output ? <>
            <div id="ran"></div>
            <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
         </> : null}
         {isExpanded ? <ExpandedBlock
            data={{ id, ...data }}
            handleClose={() => setIsExpanded(false)}
            isWarning={isWarning}
            styleParent={(data.value?.files?.length ? { right: '-101.5px' } : {})}
            style={{ width: '350px' }}
         ><Styled>
            <Select
               title="Folder"
               data={data.value?.folders || []}
               handleSelectItem={handleSelectFolder}
               isSmall={true}
               emptyMsg={foldersLoaded ? 'No knowledge folders found' : 'Loading folders...'}
            />
            {hasFolderSelected && isLoadingFiles ? (
               <p className="folderHint">Loading files...</p>
            ) : null}
            {!isAgentDecide && data.value?.files?.length ? <div className="filesEdit">
               <h3 className="title" style={{ backgroundColor: data.background, color: '#fff' }}>Files ({filesLengthActive}/{data.value?.files?.length})</h3>
               <div className="scroll nowheel">
                  <Checkboxs
                     data={data.value.files}
                     handelCheckbox={(fileId: any, value?: boolean) => {
                        updateNodeValue(id, setNodes, {
                           files: data.value.files.map((item: any) => {
                              if (item.id === fileId) return ({ ...item, isActive: !value })
                              else return item;
                           })
                        });
                     }}
                  />
               </div>
            </div> : hasFolderSelected && foldersLoaded && !isLoadingFiles ? (
               <p className="folderHint warn">This folder has no files yet. Upload files in Knowledge Base, then index them.</p>
            ) : null}
            {hasFolderSelected && indexFailed ? (
               <p className="folderHint warn">Folder is selected ({filesLengthActive} files) but indexing failed. Re-index from Knowledge Base so search works.</p>
            ) : hasFolderSelected && filesLengthActive > 0 && activeFolder?.isIndexed === false && indexingStatus === 'indexing' ? (
               <p className="folderHint">Indexing in progress…</p>
            ) : hasFolderSelected && filesLengthActive > 0 && activeFolder?.isIndexed ? (
               <p className="folderHint">Folder ready · {filesLengthActive} active file{filesLengthActive === 1 ? '' : 's'} · indexed</p>
            ) : null}
         </Styled></ExpandedBlock> : null}
      </Main>
   </>);
}
